import { EditableText, defineBlock } from "eddev/blocks"
import { tv } from "@utils/tw"
import { ComponentPropsWithoutRef, forwardRef, useEffect, useState } from "react"
import { CLOSE } from "@components/atoms/Icons"
import { useEvData, useRoadSafetyData, useTransportData } from "@hooks/queries"
import { format } from "date-fns"
import { twMerge } from "tailwind-merge"

export const meta: BlockMeta = {
  title: "Data Header",
  category: "common",
  icon: "car",
  tags: ["root"],
  editMode: "preview",
  conditions: {
    postTypes: ["page", "post"],
    multiple: false,
  },
}

export default defineBlock("data/data-header", (props) => {
  const [accordionState, setAccordionState] = useState<boolean>(false)
  const [date, setDate] = useState<Date | string | null>(null)
  const evIndexData = useEvData(undefined)
  const roadSafetyData = useRoadSafetyData(undefined)
  const transportAffordabilityData = useTransportData(undefined)
  const template = props.page?.template?.templateName

  useEffect(() => {
    if (template === "Data: Road Safety") {
      if (roadSafetyData.data?.roadSafetyData) {
        const dataset = roadSafetyData.data.roadSafetyData.map((data) => JSON.parse(data?.data ?? ""))
        const returnedData = [] as Date[]
        dataset.forEach((line: any) => {
          if (!line["Date"]) return
          const [m, y] = line["Date"].split("-")
          const date = new Date(`${m}-01-${y}`)
          returnedData.push(date)
        })
        setDate(
          returnedData.sort((a: Date, b: Date) => {
            return a.getTime() - b.getTime()
          })[returnedData.length - 1],
        )
      }
    }
    if (template === "Data: Transport Affordability") {
      if (transportAffordabilityData.data?.transportAffordabilityData) {
        const dataset = transportAffordabilityData.data.transportAffordabilityData.map((data) =>
          JSON.parse(data?.data ?? ""),
        )

        const sorted = Object.keys(dataset[0])
          .filter((key) => key.startsWith("Q"))
          .sort((a, b) => {
            // Extract year and quarter from strings
            const [, quarterA, yearA] = a.match(/Q(\d+) (\d+)/) ?? []
            const [, quarterB, yearB] = b.match(/Q(\d+) (\d+)/) ?? []
            // Compare years first
            if (yearA !== yearB) {
              return parseInt(yearA) - parseInt(yearB)
            }
            // If years are the same, compare quarters
            return parseInt(quarterA) - parseInt(quarterB)
          })
        setDate(sorted[sorted.length - 1])
      }
    }
    if (template === "Data: EV Index") {
      if (evIndexData.data?.evIndexData) {
        const dataset = evIndexData.data.evIndexData.map((data) => JSON.parse(data?.data ?? ""))

        const sorted = Object.keys(dataset[0])
          .filter((key) => key.startsWith("Q"))
          .sort((a, b) => {
            // Extract year and quarter from strings
            const [, quarterA, yearA] = a.match(/Q(\d+) (\d+)/) ?? []
            const [, quarterB, yearB] = b.match(/Q(\d+) (\d+)/) ?? []
            // Compare years first
            if (yearA !== yearB) {
              return parseInt(yearA) - parseInt(yearB)
            }
            // If years are the same, compare quarters
            return parseInt(quarterA) - parseInt(quarterB)
          })
        setDate(sorted[sorted.length - 1])
      }
    }
  }, [
    template,
    roadSafetyData.data?.roadSafetyData,
    evIndexData.data?.evIndexData,
    transportAffordabilityData.data?.transportAffordabilityData,
  ])

  return (
    <div className={twMerge("w-full", props.lightDark && "bg-black [&_h1]:!text-white [&_p]:!text-white")}>
      <div className="grid-auto px-4 md:px-6 xxl:px-0 !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px] gap-6 !text-center py-7">
        <div className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3">
          <EditableText as="h1" className="type-title-xl" store="title" placeholder="Enter title" />
        </div>

        <div className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3">
          <Accordion open={accordionState}>
            <button onClick={() => setAccordionState(!accordionState)}>
              {CLOSE}{" "}
              <EditableText
                as="span"
                store="trigger"
                placeholder="About this dashboard"
                defaultValue="About this dashboard"
              />
            </button>

            <div>
              <EditableText as="p" store="accordion_content" placeholder="Enter about text" />
              {date && (
                <>
                  {date && template === "Data: Road Safety" && (
                    <span>(as at end {format(date as Date, "MMMM yyyy")})</span>
                  )}
                  {date && template === "Data: Transport Affordability" && (
                    <span>Per household ({date as String})</span>
                  )}
                  {date && template === "Data: EV Index" && <span>Last updated {date as String}</span>}
                </>
              )}
            </div>
          </Accordion>
        </div>
      </div>
    </div>
  )
})

const accordionStyles = tv({
  base: [
    "[&_button]:appearance-none [&_button]:border-none [&_button]:bg-transparent [&_button]:type-label-caps [&_button]:text-[12px] [&_button]:text-dark-grey [&_button]:inline-flex [&_button]:items-center",
    "[&_button_svg]:text-yellow [&_button_svg]:[transform:rotate(-45deg)] [&_button_svg]:transition-all [&_button_svg]:duration-[350ms] [&_button_svg]:ease-movement",
    "[&_button_span]:ml-2",
    "[&_div]:max-h-0 [&_div]:overflow-hidden [&_div]:transition-all [&_div]:duration-[350ms] [&_div]:ease-movement",
    "[&_div>p]:my-5",
    "[&_div>p_a]:text-yellow [&_div>p_a]:underline [&_div>p_a:hover]:no-underline",
    "[&_div>span]:text-mid-grey [&_div>span]:type-body-xs",
  ],
  variants: {
    open: {
      true: "[&_button_svg]:[transform:rotate(0)] [&_div]:max-h-[100vh]",
    },
  },
})

const Accordion = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div"> & { open?: boolean }>(
  ({ open, className, ...props }, ref) => <div ref={ref} className={accordionStyles({ open, className })} {...props} />,
)
Accordion.displayName = "Accordion"
